import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { wallTimeToUtcIso } from '@resource-kit/controllers';
import { useToday } from '@resource-kit/controllers/react';
import { useIsPhone } from '@hooks/useMediaQuery';
import { useMinuteNow } from '@hooks/useMinuteNow';
import { formatRelativeEta, formatTime } from '@lib/datetime';
import { phoneDriverPath } from '@lib/routes';
import type { FulfillmentJob } from '@/services';
import { flowGateTime } from '../board/flowCopy';
import { useDeliveryFlow } from '../hooks/useDeliveryFlow';
import { useRouteEta } from '../hooks/useRouteEta';
import { DELIVERY_PROGRESS_KIND, deriveDeliveryProgress } from '../progress/deliveryProgress';

const DASH = '—';
const ETA_STATUSES: readonly FulfillmentJob['status'][] = [
  'dispatched',
  'in_progress',
  'completed',
];

/**
 * The job detail "delivery progress" tab (#2677), first on the rail so the
 * detail page opens on it, on a phone and on a desktop alike. The ETA read is
 * the dispatch page's own (same query key) and is only made for a job on the
 * road today.
 *
 * Mounted by `../jobResource`, so this module must not import `../jobResource`,
 * `../hooks/useRouteMonitoringJoin` or `../panes/enRouteModel` (they import
 * `../jobResource` back). The window end is computed here the way
 * `enRouteTimes` does it.
 */
export function DeliveryProgress({ record }: { record: FulfillmentJob }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const nowMs = useMinuteNow();
  const today = useToday();
  const enabled =
    record.shipping_date === today &&
    record.onfleet_task_id !== null &&
    ETA_STATUSES.includes(record.status);
  const eta = useRouteEta(enabled);
  const flow = useDeliveryFlow();
  const windowStart = flow.data?.delivery_window_start ?? null;
  const windowEnd = flow.data ? flowGateTime(flow.data, 'delivery_window_end') : null;
  const windowEndMs =
    flow.data && windowEnd !== null
      ? Date.parse(wallTimeToUtcIso(`${today}T${windowEnd}:00.000`, flow.data.timezone))
      : null;

  const state = deriveDeliveryProgress({
    job: record,
    today,
    eta,
    flowPending: flow.isPending,
    windowEndMs,
    nowMs,
  });

  /**
   * The driver row. On a phone it is a full-width row that opens that driver's
   * list (a 44px touch target); a desktop has no such view, so it is text.
   */
  const driverRow = (name: string | null, workerId: string | null, position: string): ReactNode => {
    const text = (
      <span className="min-w-0 flex-1 truncate">
        <span className="font-medium">{name ?? DASH}</span>
        {` · ${position}`}
      </span>
    );
    return isPhone && workerId !== null ? (
      <Link
        to={phoneDriverPath(workerId)}
        className="-mx-2 flex min-h-11 items-center gap-2 rounded-md px-2 text-sm text-foreground no-underline hover:bg-muted"
      >
        {text}
        <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      </Link>
    ) : (
      <div className="flex text-sm">{text}</div>
    );
  };

  let body: ReactNode;
  switch (state.kind) {
    case DELIVERY_PROGRESS_KIND.loading:
      body = (
        <div aria-busy="true" className="flex flex-col gap-2">
          <div className="h-7 w-32 animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-56 animate-pulse rounded-md bg-muted" />
        </div>
      );
      break;
    case DELIVERY_PROGRESS_KIND.onTime:
    case DELIVERY_PROGRESS_KIND.late: {
      const late = state.kind === DELIVERY_PROGRESS_KIND.late;
      const relative = formatRelativeEta(state.etaMs, new Date(nowMs));
      const position = state.active
        ? t('job.progress.active')
        : t('job.progress.ahead', { count: state.ahead });
      body = (
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline gap-2">
            <span
              data-late={late ? 'true' : undefined}
              className={`text-2xl font-semibold ${late ? 'text-warning' : ''}`}
            >
              {formatTime(state.etaMs)}
            </span>
            {relative && <span className="text-sm text-muted-foreground">{relative.text}</span>}
          </div>
          {!late && windowEnd !== null && (
            <span className="text-sm text-muted-foreground">
              {t('job.progress.inWindow', { start: windowStart ?? DASH, end: windowEnd })}
            </span>
          )}
          {late && (
            <span className="text-sm text-muted-foreground">
              {t('job.progress.lateBy', { end: windowEnd ?? DASH, minutes: state.minutesLate })}
            </span>
          )}
          {driverRow(state.driverName, state.workerId, position)}
        </div>
      );
      break;
    }
    case DELIVERY_PROGRESS_KIND.delivered:
      body = (
        <div className="flex flex-col gap-1">
          <span className="text-lg font-semibold">
            {state.completedAt === null
              ? t('job.progress.delivered')
              : t('job.progress.deliveredAt', { time: formatTime(state.completedAt) })}
          </span>
          {state.driverName !== null && (
            <span className="text-sm text-muted-foreground">
              {t('job.progress.deliveredBy', { name: state.driverName })}
            </span>
          )}
        </div>
      );
      break;
    case DELIVERY_PROGRESS_KIND.notYet:
      body = <p className="m-0 text-sm">{t('job.progress.notYet', { date: state.date })}</p>;
      break;
    case DELIVERY_PROGRESS_KIND.unavailable:
      body = (
        <div className="flex flex-col items-start gap-2">
          <p className="m-0 text-sm">
            {t('job.progress.unavailable', { name: state.driverName ?? DASH })}
          </p>
          <button
            type="button"
            onClick={() => void eta.refetch()}
            className="inline-flex h-11 items-center rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground"
          >
            {t('ui.retry')}
          </button>
        </div>
      );
      break;
    case DELIVERY_PROGRESS_KIND.closed:
      body = <p className="m-0 text-sm">{t('job.progress.closed')}</p>;
      break;
  }

  return <div data-testid="job-delivery-progress">{body}</div>;
}
